/**
 * @file api-firestore.ts
 * @description Firestore client for CRUD operations on plans and favorites
 * Replaces the old REST API based on Express + LowDB
 * @author XiaVan Team
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy,
} from 'firebase/firestore'
import { db, auth } from './firebase'
import type { Plan } from '@/types/plan'

/**
 * Get all plans from Firestore
 * @returns Array of plans
 */
export async function getPlanes(): Promise<Plan[]> {
  try {
    const planesRef = collection(db, 'plans')
    const q = query(planesRef, orderBy('id', 'asc'))
    const snapshot = await getDocs(q)
    
    const planes: Plan[] = []
    snapshot.forEach((doc) => {
      const data = doc.data() as Plan
      planes.push(data)
    })
    
    return planes
  } catch (error) {
    console.error('Error fetching plans:', error)
    throw error
  }
}

/**
 * Get a specific plan by ID
 * @param id - Plan ID
 * @returns Found plan
 */
export async function getPlan(id: number): Promise<Plan> {
  try {
    const planRef = doc(db, 'plans', id.toString())
    const planDoc = await getDoc(planRef)
    
    if (!planDoc.exists()) {
      throw new Error(`Plan ${id} not found`)
    }
    
    return planDoc.data() as Plan
  } catch (error) {
    console.error(`Error fetching plan ${id}:`, error)
    throw error
  }
}

/**
 * Update plan images
 * @param id - Plan ID
 * @param imagenes - Array of image URLs
 */
export async function updatePlanImages(id: number, imagenes: string[]): Promise<void> {
  try {
    const planRef = doc(db, 'plans', id.toString())
    await updateDoc(planRef, {
      imagenes,
      updatedAt: new Date().toISOString(),
    })
  } catch (error) {
    console.error(`Error updating images for plan ${id}:`, error)
    throw error
  }
}

/**
 * Get current user's favorites
 * Reads from 'usuarios/{userId}/favoritos' document
 * @returns Array of favorite plan IDs
 */
export async function getFavorites(): Promise<number[]> {
  try {
    const user = auth.currentUser
    if (!user) return []

    const favoritesRef = doc(db, 'usuarios', user.uid, 'preferencias', 'favoritos')
    const favoritesDoc = await getDoc(favoritesRef)
    
    if (!favoritesDoc.exists()) {
      return []
    }
    
    return favoritesDoc.data()?.planIds || []
  } catch (error) {
    console.error('Error fetching favorites:', error)
    return []
  }
}

/**
 * Toggle favorite: add or remove plan from favorites
 * Saves to Firestore under 'usuarios/{userId}/preferencias/favoritos'
 * @param id - Plan ID
 * @returns Updated favorites array
 */
export async function toggleFavorite(id: number): Promise<{ favoritos: number[] }> {
  try {
    const user = auth.currentUser
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Get current favorites
    const currentFavorites = await getFavorites()
    
    // Toggle: if exists, remove; if not exists, add
    const newFavorites = currentFavorites.includes(id)
      ? currentFavorites.filter((fav) => fav !== id)
      : [...currentFavorites, id]
    
    // Save to Firestore
    const favoritesRef = doc(db, 'usuarios', user.uid, 'preferencias', 'favoritos')
    await setDoc(favoritesRef, {
      planIds: newFavorites,
      updatedAt: new Date().toISOString(),
    })
    
    return { favoritos: newFavorites }
  } catch (error) {
    console.error('Error toggling favorite:', error)
    throw error
  }
}
